import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Linkedin, Instagram, ArrowUpRight, Shield } from 'lucide-react';
import { companyInfo, footerLinks } from '../../data/mock';

// Company Logo
const LOGO_COMPACT = "https://customer-assets.emergentagent.com/job_techedit-1/artifacts/xcqi87qo_image.png";

const Footer = () => {
  const isoCertifications = [
    { code: 'ISO 9001:2015', name: 'Quality Management' },
    { code: 'ISO/IEC 27001:2022', name: 'Information Security' },
    { code: 'ISO/IEC 20000-1:2018', name: 'IT Service Management' }
  ];

  return (
    <footer className="bg-gray-900 text-white relative overflow-hidden">
      {/* Decorative Elements */}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#ff6b35]/10 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-[#ff6b35]/5 rounded-full blur-3xl"></div>
      
      {/* Main Footer */}
      <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12 lg:gap-8">
          {/* Brand */}
          <div className="lg:col-span-2">
            <Link to="/" className="inline-flex items-center gap-3 mb-6">
              <img 
                src={LOGO_COMPACT} 
                alt="UZVI Services" 
                className="h-12 w-auto object-contain"
                data-testid="footer-logo"
              />
              <span className="text-xl font-bold">
                UZVI<span className="text-[#ff6b35]"> Services</span>
              </span>
            </Link>
            <p className="text-gray-400 text-sm leading-relaxed mb-6">
              Empowering businesses through innovative technology solutions. SAP, Cloud, Web & Mobile development experts serving customers worldwide.
            </p>
            
            {/* ISO Certifications */}
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-3">
                <Shield className="w-4 h-4 text-[#ff6b35]" />
                <span className="text-sm font-semibold text-gray-300">ISO Certified</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {isoCertifications.map((cert) => (
                  <div 
                    key={cert.code}
                    className="px-3 py-1.5 bg-white/5 rounded-lg border border-white/10 hover:border-[#ff6b35]/30 transition-colors cursor-default"
                    title={cert.name}
                  >
                    <span className="text-xs font-medium text-gray-300">{cert.code}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex gap-3">
              <a
                href="https://in.linkedin.com/company/uzviservices"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center hover:bg-[#ff6b35] transition-colors group"
                data-testid="social-linkedin"
              >
                <Linkedin className="w-5 h-5 text-gray-400 group-hover:text-white" />
              </a>
              <a
                href="https://www.instagram.com/uzvi_services/?hl=en"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center hover:bg-[#ff6b35] transition-colors group"
                data-testid="social-instagram"
              >
                <Instagram className="w-5 h-5 text-gray-400 group-hover:text-white" />
              </a>
            </div>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-sm font-semibold uppercase tracking-wider text-gray-400 mb-6">Services</h3>
            <ul className="space-y-3">
              {footerLinks.services.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-gray-300 hover:text-[#ff6b35] text-sm transition-colors inline-flex items-center gap-1 group"
                  >
                    {link.name}
                    <ArrowUpRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div>
            <h3 className="text-sm font-semibold uppercase tracking-wider text-gray-400 mb-6">Company</h3>
            <ul className="space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-gray-300 hover:text-[#ff6b35] text-sm transition-colors inline-flex items-center gap-1 group"
                  >
                    {link.name}
                    <ArrowUpRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="text-sm font-semibold uppercase tracking-wider text-gray-400 mb-6">Get in Touch</h3>
            <ul className="space-y-4">
              <li>
                <a href={`tel:${companyInfo.phone}`} className="flex items-start gap-3 text-gray-300 hover:text-white transition-colors">
                  <div className="w-8 h-8 rounded-lg bg-[#ff6b35]/20 flex items-center justify-center flex-shrink-0">
                    <Phone className="w-4 h-4 text-[#ff6b35]" />
                  </div>
                  <span className="text-sm">{companyInfo.phone}</span>
                </a>
              </li>
              <li>
                <a href={`mailto:${companyInfo.email}`} className="flex items-start gap-3 text-gray-300 hover:text-white transition-colors">
                  <div className="w-8 h-8 rounded-lg bg-[#ff6b35]/20 flex items-center justify-center flex-shrink-0">
                    <Mail className="w-4 h-4 text-[#ff6b35]" />
                  </div>
                  <span className="text-sm">{companyInfo.email}</span>
                </a>
              </li>
              <li>
                <div className="flex items-start gap-3 text-gray-300">
                  <div className="w-8 h-8 rounded-lg bg-[#ff6b35]/20 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-4 h-4 text-[#ff6b35]" />
                  </div>
                  <span className="text-sm">{companyInfo.location}</span>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-gray-500 text-sm">
              © {new Date().getFullYear()} UZVI Services LLP. All rights reserved.
            </p>
            <div className="flex gap-6">
              <Link to="/privacy" className="text-gray-500 hover:text-gray-300 text-sm transition-colors">
                Privacy Policy
              </Link>
              <Link to="/terms" className="text-gray-500 hover:text-gray-300 text-sm transition-colors">
                Terms of Service
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
